"use client";

import { useEffect, useState, useSyncExternalStore } from "react";
import {
  PRODUCT_SURFACE_LOCK_COOKIE_NAME,
  PRODUCT_SURFACE_LOCK_ENABLED,
  PRODUCT_SURFACE_LOCK_UNLOCK_VALUE,
  isProductSurfaceLockActive,
  readProductSurfaceLockCookie,
} from "@/lib/temporaryProductLock";
import {
  RISK_ASSESSMENT_SURVEY_COOKIE_NAME,
  RISK_ASSESSMENT_SURVEY_DISABLED_VALUE,
  isRiskAssessmentSurveyEnabled,
} from "@/lib/riskAssessmentSurvey";

declare global {
  interface Window {
    lock?: boolean;
    survey?: boolean;
    riskVideo?: boolean;
    hint?: () => Readonly<Record<string, string>>;
  }
}

const CONSOLE_UNLOCK_MAX_AGE_SECONDS = 365 * 24 * 60 * 60;

const CONSOLE_MAGIC_HINTS = [
  {
    usage: "hint();",
    description: "사용 가능한 SEEXR 개발자 콘솔 명령어를 표시합니다.",
  },
  {
    usage: "lock = false; / lock = true;",
    description: "임시 제품 UI 잠금을 해제하거나 다시 잠급니다.",
  },
  {
    usage: "survey = false; / survey = true;",
    description: "위험성평가 설문 노출을 끄거나 켭니다.",
  },
  {
    usage: "riskVideo = false; / riskVideo = true;",
    description:
      "위험성평가 교육영상 생성 버튼을 숨기거나 표시합니다. 기본값은 false입니다.",
  },
] as const;
const RISK_EDUCATION_VIDEO_COOKIE_NAME = "seexr_risk_education_video";
const RISK_EDUCATION_VIDEO_ENABLED_VALUE = "enabled";

function getBrowserLockState(): boolean {
  return isProductSurfaceLockActive(
    readProductSurfaceLockCookie(document.cookie),
  );
}

function readCookie(cookieName: string): string | undefined {
  const prefix = `${cookieName}=`;
  const match = document.cookie
    .split(";")
    .map((part) => part.trim())
    .find((part) => part.startsWith(prefix));

  return match?.slice(prefix.length);
}

function getBrowserSurveyState(): boolean {
  return isRiskAssessmentSurveyEnabled(
    readCookie(RISK_ASSESSMENT_SURVEY_COOKIE_NAME),
  );
}

function getBrowserRiskVideoState(): boolean {
  return (
    readCookie(RISK_EDUCATION_VIDEO_COOKIE_NAME) ===
    RISK_EDUCATION_VIDEO_ENABLED_VALUE
  );
}

function getCookieSuffix(): string {
  return `Path=/; SameSite=Lax${window.location.protocol === "https:" ? "; Secure" : ""}`;
}

function getConsoleMagicHint(): Readonly<Record<string, string>> {
  return Object.fromEntries(
    CONSOLE_MAGIC_HINTS.map(({ usage, description }) => [usage, description]),
  );
}

export function ProductSurfaceLockConsole() {
  useEffect(() => {
    const previousDescriptor = Object.getOwnPropertyDescriptor(window, "lock");
    const previousSurveyDescriptor = Object.getOwnPropertyDescriptor(
      window,
      "survey",
    );
    const previousRiskVideoDescriptor = Object.getOwnPropertyDescriptor(
      window,
      "riskVideo",
    );
    const previousHintDescriptor = Object.getOwnPropertyDescriptor(
      window,
      "hint",
    );

    Object.defineProperty(window, "lock", {
      configurable: true,
      get: getBrowserLockState,
      set(value: boolean) {
        if (value === false) {
          document.cookie = `${PRODUCT_SURFACE_LOCK_COOKIE_NAME}=${PRODUCT_SURFACE_LOCK_UNLOCK_VALUE}; Max-Age=${CONSOLE_UNLOCK_MAX_AGE_SECONDS}; ${getCookieSuffix()}`;
        } else if (value === true) {
          document.cookie = `${PRODUCT_SURFACE_LOCK_COOKIE_NAME}=; Max-Age=0; ${getCookieSuffix()}`;
        } else {
          throw new TypeError("lock에는 true 또는 false만 지정할 수 있습니다.");
        }

        window.location.reload();
      },
    });

    Object.defineProperty(window, "survey", {
      configurable: true,
      get: getBrowserSurveyState,
      set(value: boolean) {
        if (value === false) {
          document.cookie = `${RISK_ASSESSMENT_SURVEY_COOKIE_NAME}=${RISK_ASSESSMENT_SURVEY_DISABLED_VALUE}; Max-Age=${CONSOLE_UNLOCK_MAX_AGE_SECONDS}; ${getCookieSuffix()}`;
        } else if (value === true) {
          document.cookie = `${RISK_ASSESSMENT_SURVEY_COOKIE_NAME}=; Max-Age=0; ${getCookieSuffix()}`;
        } else {
          throw new TypeError("survey에는 true 또는 false만 지정할 수 있습니다.");
        }

        window.location.reload();
      },
    });

    Object.defineProperty(window, "riskVideo", {
      configurable: true,
      get: getBrowserRiskVideoState,
      set(value: boolean) {
        if (value === true) {
          document.cookie = `${RISK_EDUCATION_VIDEO_COOKIE_NAME}=${RISK_EDUCATION_VIDEO_ENABLED_VALUE}; Max-Age=${CONSOLE_UNLOCK_MAX_AGE_SECONDS}; ${getCookieSuffix()}`;
        } else if (value === false) {
          document.cookie = `${RISK_EDUCATION_VIDEO_COOKIE_NAME}=; Max-Age=0; ${getCookieSuffix()}`;
        } else {
          throw new TypeError(
            "riskVideo에는 true 또는 false만 지정할 수 있습니다.",
          );
        }

        window.location.reload();
      },
    });

    Object.defineProperty(window, "hint", {
      configurable: true,
      writable: false,
      value: getConsoleMagicHint,
    });

    return () => {
      if (previousDescriptor) {
        Object.defineProperty(window, "lock", previousDescriptor);
      } else {
        delete window.lock;
      }
      if (previousSurveyDescriptor) {
        Object.defineProperty(window, "survey", previousSurveyDescriptor);
      } else {
        delete window.survey;
      }
      if (previousRiskVideoDescriptor) {
        Object.defineProperty(window, "riskVideo", previousRiskVideoDescriptor);
      } else {
        delete window.riskVideo;
      }
      if (previousHintDescriptor) {
        Object.defineProperty(window, "hint", previousHintDescriptor);
      } else {
        delete window.hint;
      }
    };
  }, []);

  return null;
}

export function useProductSurfaceLockActive(): boolean {
  const [isLocked, setIsLocked] = useState(PRODUCT_SURFACE_LOCK_ENABLED);

  useEffect(() => {
    setIsLocked(getBrowserLockState());
  }, []);

  return isLocked;
}

export function useRiskAssessmentSurveyEnabled(): boolean {
  const [isEnabled, setIsEnabled] = useState(true);

  useEffect(() => {
    setIsEnabled(getBrowserSurveyState());
  }, []);

  return isEnabled;
}

export function useRiskEducationVideoEnabled(): boolean {
  return useSyncExternalStore(
    () => () => undefined,
    getBrowserRiskVideoState,
    () => false,
  );
}
